import React, { useState } from 'react';
import Classes from './RateExperiencePopup.module.css';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';

const overlayVar = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
};

const cardVar = {
    hidden: { opacity: 0, y: 28, scale: 0.96, rotateX: -8, filter: 'blur(8px)' },
    show: {
        opacity: 1, y: 0, scale: 1, rotateX: 0, filter: 'blur(0px)',
        transition: { type: 'spring', stiffness: 520, damping: 32, mass: 0.7 }
    },
    exit: { opacity: 0, y: 18, scale: 0.98, transition: { duration: 0.2 } }
};

const headerReveal = {
    hidden: { opacity: 0, y: -8, clipPath: 'inset(0 0 100% 0 round 16px)' },
    show: {
        opacity: 1, y: 0, clipPath: 'inset(0 0 0% 0 round 16px)',
        transition: { duration: 0.5, ease: 'easeOut', delay: 0.05 }
    }
};

const bodyStagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.06, delayChildren: 0.15 } }
};

const item = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } }
};

const digitVar = {
    hidden: { opacity: 0, y: 12, scale: 0.95 },
    show: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 600, damping: 24 } }
};

export default function RateExperiencePopup({ handleClose, handleSubmit }) {
    const { user } = useSelector((state) => state.auth);
    const mentorName = user?.menteeProfile?.basicInfo?.name || 'Mentor Name';

    const [rating, setRating] = useState(null); // 0..5
    const ratings = [0, 1, 2, 3, 4, 5];

    const handleBgClick = () => {/* intentional noop to prevent accidental close */ };
    const handleCardClick = (e) => e.stopPropagation();

    const onSubmit = () => {
        if (rating !== null) {
            handleSubmit(rating);
        }
    };

    return (
        <motion.div
            variants={overlayVar}
            initial="hidden"
            animate="show"
            exit="exit"
            className={Classes.popup}
            onClick={handleBgClick}
        >
            <motion.div
                variants={cardVar}
                className={Classes.card}
                onClick={handleCardClick}
                role="dialog"
                aria-modal="true"
                aria-label="Rate your experience"
            >
                <button className={Classes.close} onClick={handleClose} aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" height="25px" fill="white" viewBox="0 0 384 512">
                        <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
                    </svg>
                </button>

                <motion.div className={Classes.bgPopup} variants={headerReveal}>
                    <h3 className={Classes.title}>Rate Your Experience</h3>
                    <div className={Classes.curveCon} />
                </motion.div>

                <motion.div className={Classes.cardCon} variants={bodyStagger} initial="hidden" animate="show">
                    <motion.h5 className={Classes.title2} variants={item}>
                        How was your experience with <span className={Classes.strong}>{mentorName}</span>?
                    </motion.h5>

                    <motion.div className={Classes.ratingWrap} variants={item}>
                        <motion.div
                            className={Classes.ratingRow}
                            role="radiogroup"
                            aria-label="Rate from 0 to 5"
                            variants={bodyStagger}
                        >
                            {ratings.map((n) => (
                                <motion.button
                                    key={n}
                                    type="button"
                                    onClick={() => setRating(n)}
                                    className={`${Classes.ratingBtn} ${rating === n ? Classes.ratingSelected : ''}`}
                                    role="radio"
                                    aria-checked={rating === n}
                                    variants={digitVar}
                                    whileHover={{ y: -2, scale: 1.03 }}
                                    whileTap={{ scale: 0.96 }}
                                >
                                    {n}
                                </motion.button>
                            ))}
                        </motion.div>

                        <div className={Classes.ratingLabels}>
                            <span>Awful</span>
                            <span>Amazing</span>
                        </div>
                    </motion.div>

                    <motion.div className={Classes.flexAlign} style={{ marginTop: '1.6rem' }} variants={item}>
                        <button type="button" onClick={handleClose} className={Classes.linkBtn}>
                            Skip
                        </button>
                        <motion.button
                            type="button"
                            onClick={onSubmit}
                            className={Classes.submit}
                            disabled={rating === null}
                            animate={{ scale: rating === null ? 1 : [1, 1.05, 1] }}
                            transition={{ times: [0, 0.6, 1], duration: 0.35 }}
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            Submit
                        </motion.button>
                    </motion.div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}